import { PricelistDto } from '../common/PricePrgClient';
import { PricelistSettings } from '../features/pricelist/pricelistTypes';

export function belongsToBasePricelist(
    pricelistId: number,
    pricelists: { [pricelistId: number]: PricelistDto }
): boolean {
    const currentPricelist = pricelists[pricelistId];

    if (currentPricelist == null) {
        return false;
    }

    if (currentPricelist.isBasePricelist === true) {
        return true;
    }

    if (currentPricelist.parentPricelistId == null) {
        return false;
    }

    return belongsToBasePricelist(currentPricelist.parentPricelistId, pricelists);
}
/**
 * Returns whether the supplied parentPricelistId is parent (doesn't have to be a direct parent)
 * of the supplied pricelist id.
 *
 * @param pricelistId
 *      the pricelist id to check
 * @param parentPricelistId
 *      the parent id to match
 * @param pricelists
 *      the available pricelists
 */
export function hasParent(
    pricelistId: number,
    parentPricelistId: number,
    pricelists: { [pricelistId: number]: PricelistDto }
): boolean {
    const currentParentPricelistId = pricelists[pricelistId]?.parentPricelistId;

    if (currentParentPricelistId == null) {
        return false;
    }

    if (parentPricelistId === currentParentPricelistId) {
        return true;
    }

    return hasParent(currentParentPricelistId, parentPricelistId, pricelists);
}
/**
 * Returns the root pricelist of a pricelist or null or the current
 * pricelist if it does not have a parent
 * @param pricelistId
 *       the pricelist id to check
 * @param pricelists
 *       the available pricelists
 */
export function getPricelistRoot(
    pricelistId: number,
    pricelists: { [pricelistId: number]: PricelistDto }
): PricelistDto | null {
    const pricelist = pricelists[pricelistId];
    if (pricelist == null) {
        return null;
    }
    if (pricelist.parentPricelistId == null) {
        return pricelist;
    }
    return getPricelistRoot(pricelist.parentPricelistId, pricelists);
}

export function getPricelistTreeLevel(
    pricelistId: number,
    allPricelists: { [pricelistId: number]: PricelistDto }
): number {
    const parentPricelistId = allPricelists[pricelistId]?.parentPricelistId;
    return parentPricelistId != null ? getPricelistTreeLevel(parentPricelistId, allPricelists) + 1 : 0;
}

export function sortPricelistsHierarchically(
    pricelistIds: number[],
    allPricelists: { [pricelistId: number]: PricelistDto }
): number[] {
    return [...pricelistIds].sort((a, b) => {
        const diff = getPricelistTreeLevel(a, allPricelists) - getPricelistTreeLevel(b, allPricelists);
        if (diff !== 0) {
            return diff;
        }

        const aName = allPricelists[a]?.name ?? '';
        const bName = allPricelists[b]?.name ?? '';
        return aName.localeCompare(bName);
    });
}

export function getAllPricelistChildren(pricelistId: number, pricelists: PricelistDto[]) {
    if (pricelistId === 0) {
        return [];
    }

    const children: number[] = [];
    for (const pl of pricelists) {
        if (pl.parentPricelistId === pricelistId) {
            children.push(pl.pricelistId);
            children.push(...getAllPricelistChildren(pl.pricelistId, pricelists));
        }
    }
    return children;
}

export function getPricelistSettings(
    pricelist: PricelistDto | null,
    allPricelists: { [pricelistId: number]: PricelistDto }
): PricelistSettings | null {
    if (!pricelist) {
        return null;
    }

    const root = getPricelistRoot(pricelist.pricelistId, allPricelists);
    const mainPricelist = root?.mainCurrencyPricelistId ? allPricelists[root.mainCurrencyPricelistId] : null;

    if (root == null) {
        return pricelist;
    }

    return {
        pricelistPeriodDayLength: (mainPricelist ?? root).pricelistPeriodDayLength,
        allowedWeekdays: (mainPricelist ?? root).allowedWeekdays,
        emptyPricelistOnCreate: (mainPricelist ?? root).emptyPricelistOnCreate,
        isReadonly: (mainPricelist ?? root).isReadonly,
        hasWeekdayRestrictions: (mainPricelist ?? root).hasWeekdayRestrictions,
        isBasePricelist: root.isBasePricelist,
        ignoreAgreementPricesOnPricelistMail: root.ignoreAgreementPricesOnPricelistMail,
        useDefaultCurrencyConversion: root.useDefaultCurrencyConversion,
        mainCurrencyPricelistId: root.mainCurrencyPricelistId,
        applyCommentsToChildren: pricelist.applyCommentsToChildren,
        forbidCalculationBypass: pricelist.forbidCalculationBypass
    };
}
